import React, { useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { X, Activity } from "lucide-react";
import { maintAnalyticsApi as api } from "../../../api/maintenance/analytics";
import type { SessionDetail, MeterValuePoint } from "../../../api/maintenance/analytics";
import "../shared/maintenance-analytics.scss";

// Whatever the caller already has from the clicked row — shown immediately + as a
// fallback if the full-detail endpoint isn't reachable (e.g. mid-deploy).
export interface SessionBasic {
  session_id: number;
  station?: string | null;
  connector?: string | null;
  device?: string | null;
  user_name?: string | null;
  phone?: string | null;
  organization?: string | null;
  consumed_kwh?: number | null;
  charge_rate_kw?: number | null;
  total_cost?: number | null;
  duration_mins?: number | null;
  status?: string | null;
}

export const num = (v: number | null | undefined, d = 0) =>
  v == null ? "—" : v.toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });
export const money = (v: number | null | undefined) => (v == null ? "—" : `₹${num(v, 2)}`);
export const kw = (v: number | null | undefined) => (v == null ? "—" : `${num(v, 2)} kW`);
export const ts = (s: string | null | undefined) => (s ? new Date(s).toLocaleString("en-IN", { hour12: false }) : "—");
const clock = (s: string) => new Date(s).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });

export const statusPill = (s: string | null | undefined): string => {
  const v = (s || "").toLowerCase();
  if (v.includes("charg")) return "info";
  if (v.includes("avail") || v.includes("finish") || v.includes("complet")) return "ok";
  if (v.includes("fault") || v.includes("unavail") || v.includes("error")) return "bad";
  return "muted";
};

export const Field: React.FC<{ label: string; value: React.ReactNode; wide?: boolean; mono?: boolean }> =
  ({ label, value, wide, mono }) => (
    <div style={{ gridColumn: wide ? "1 / -1" : undefined }}>
      <div style={{ fontSize: 11, color: "#8a92a1", textTransform: "uppercase", letterSpacing: ".3px" }}>{label}</div>
      <div style={{ fontSize: 13, color: "#1f2430", fontWeight: 500, fontFamily: mono ? "monospace" : undefined, wordBreak: "break-word" }}>
        {value == null || value === "" ? "—" : value}
      </div>
    </div>
  );

export const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="ma-card" style={{ marginBottom: 12 }}>
    <div className="ma-card-h"><h3>{title}</h3></div>
    <div className="ma-card-b" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 14 }}>
      {children}
    </div>
  </div>
);

export const KPImini: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="ma-kpi"><div className="ma-kpi-label">{label}</div><div className="ma-kpi-value" style={{ fontSize: 15 }}>{value}</div></div>
);

const SessionDetailDrawer: React.FC<{ sessionId: number | null; fallback?: SessionBasic | null; onClose: () => void }> =
  ({ sessionId, fallback, onClose }) => {
    const [detail, setDetail] = useState<SessionDetail | "loading" | null>(null);
    const [meter, setMeter] = useState<MeterValuePoint[] | "loading">([]);

    useEffect(() => {
      if (sessionId == null) return;
      setDetail("loading");
      setMeter("loading");
      api.sessionDetail(sessionId).then((r) => setDetail(r.data)).catch(() => setDetail(null));
      api.meterValues(sessionId).then((r) => setMeter(r.data)).catch(() => setMeter([]));
    }, [sessionId]);

    const energySeries = useMemo(() => {
      if (meter === "loading" || !Array.isArray(meter)) return [];
      return meter.filter((m) => m.measurand === "Consumed KWH").map((m) => ({ t: clock(m.event_time), value: m.value ?? 0 }));
    }, [meter]);
    const powerSeries = useMemo(() => {
      if (meter === "loading" || !Array.isArray(meter)) return [];
      return meter.filter((m) => m.measurand === "Power").map((m) => ({ t: clock(m.event_time), value: m.value ?? 0 }));
    }, [meter]);

    if (sessionId == null) return null;
    const d = detail && detail !== "loading" ? detail : null;
    const status = d?.status ?? fallback?.status;
    // prefer full detail, fall back to the row values we already have
    const energy = d?.consumed_kwh ?? fallback?.consumed_kwh;
    const rate = d?.charge_rate_kw ?? fallback?.charge_rate_kw;
    const dur = d?.duration_mins ?? fallback?.duration_mins;
    const cost = d?.total_cost ?? fallback?.total_cost;

    return (
      <div className="ma-drawer-bg" onClick={onClose}>
        <div className="ma-drawer" onClick={(e) => e.stopPropagation()}>
          <div className="ma-drawer-h">
            <h3>Session #{sessionId}{status ? <> · <span className={`ma-pill ${statusPill(status)}`}>{status}</span></> : ""}</h3>
            <button onClick={onClose}><X size={18} /></button>
          </div>
          <div className="ma-drawer-b ma">
            {/* KPIs always show (from detail or the row) */}
            <div className="ma-kpis">
              <KPImini label="Energy" value={`${num(energy, 2)} kWh`} />
              <KPImini label="Charge rate" value={kw(rate)} />
              <KPImini label="Duration" value={dur != null ? `${dur}m` : "—"} />
              <KPImini label="Cost" value={money(cost)} />
            </div>

            {detail === "loading" && <p className="ma-hint">Loading full detail…</p>}
            {detail === null && (
              <>
                <div className="ma-note">Full detail isn't available (the session-detail API may still be deploying). Showing what's on the row + the meter curve below.</div>
                <Section title="Session (from row)">
                  <Field label="Session ID" value={sessionId} mono />
                  <Field label="Station" value={fallback?.station} />
                  <Field label="Device" value={fallback?.device} />
                  <Field label="Connector" value={fallback?.connector} />
                  <Field label="User" value={fallback?.user_name} />
                  <Field label="Phone" value={fallback?.phone} mono />
                  <Field label="Organization" value={fallback?.organization} />
                </Section>
              </>
            )}

            {d && (
              <>
                <Section title="Session">
                  <Field label="Session ID" value={d.session_id} mono />
                  <Field label="GUID" value={d.session_guid} mono wide />
                  <Field label="Started" value={ts(d.session_started_on)} />
                  <Field label="Stopped" value={ts(d.session_stopped_on)} />
                  <Field label="Duration" value={d.duration_mins != null ? `${d.duration_mins} min` : "—"} />
                  <Field label="Stop reason" value={d.stop_reason} />
                </Section>
                <Section title="User">
                  <Field label="Name" value={d.user_name} />
                  <Field label="Phone" value={d.phone} mono />
                  <Field label="Email" value={d.email} />
                  <Field label="Organization" value={d.organization} />
                </Section>
                <Section title="Charger">
                  <Field label="Station" value={d.station} />
                  <Field label="Address" value={d.text_address} wide />
                  <Field label="Device" value={d.device_name || d.device} />
                  <Field label="OCPP" value={d.ocpp_version} />
                  <Field label="Connector" value={d.connector_name} />
                  <Field label="Socket" value={d.socket_name} />
                  <Field label="Capacity" value={d.capacity_kwh != null ? `${d.capacity_kwh} kW` : "—"} />
                  <Field label="Connector desc" value={d.connector_description} />
                </Section>
                <Section title="Vehicle">
                  <Field label="Vehicle" value={d.vehicle_name} />
                  <Field label="Model" value={d.vehicle_model} />
                </Section>
                <Section title="Energy & cost">
                  <Field label="Meter start" value={d.meter_start != null ? num(d.meter_start) : "—"} />
                  <Field label="Meter stop" value={d.meter_stop != null ? num(d.meter_stop) : "—"} />
                  <Field label="Consumed" value={`${num(d.consumed_kwh, 2)} kWh`} />
                  <Field label="Rate" value={d.rate_kwh != null ? `₹${num(d.rate_kwh, 2)} / kWh` : "—"} />
                  <Field label="Total cost" value={money(d.total_cost)} />
                  <Field label="Charge rate" value={kw(d.charge_rate_kw)} />
                </Section>
              </>
            )}

            {/* meter chart ALWAYS renders from its own fetch — independent of the detail call */}
            <div className="ma-card">
              <div className="ma-card-h"><h3>Meter values over time</h3></div>
              <div className="ma-card-b">
                {meter === "loading" ? <p className="ma-loading">Loading…</p> :
                  (energySeries.length || powerSeries.length) ? (
                    <>
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" vertical={false} />
                          <XAxis dataKey="t" type="category" allowDuplicatedCategory={false} stroke="#8a92a1" fontSize={10} minTickGap={30} />
                          <YAxis stroke="#8a92a1" fontSize={11} width={44} />
                          <Tooltip />
                          {!!energySeries.length && <Line data={energySeries} dataKey="value" name="Consumed kWh" type="monotone" stroke="#5554d3" strokeWidth={2} dot={false} />}
                          {!!powerSeries.length && <Line data={powerSeries} dataKey="value" name="Power kW" type="monotone" stroke="#e65100" strokeWidth={2} dot={false} />}
                        </LineChart>
                      </ResponsiveContainer>
                      <div className="ma-legend" style={{ marginTop: 6 }}>
                        {!!energySeries.length && <span className="ma-lg"><i style={{ background: "#5554d3" }} />Consumed kWh</span>}
                        {!!powerSeries.length && <span className="ma-lg"><i style={{ background: "#e65100" }} />Power kW</span>}
                      </div>
                    </>
                  ) : <p className="ma-empty"><Activity size={16} /> No meter values recorded for this session</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

export default SessionDetailDrawer;
